import { AlignmentTransform } from "@/modes/alignment-modes-commons/alignment-transform";
import { TwoPointsPairsAlignmentAnchorPositions } from "@/modes/alignment-modes-commons/compute-split-screen-alignment";
import {
  AlignmentViewLayout,
  SplitDirection,
} from "@/store/modes/alignment-ui-types";
import { RootState } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import {
  AlignmentReference,
  CloudCadAlignmentStep,
} from "./cloud-cad-alignment-mode-slice";

/**
 * @returns the current cloud-and-CAD alignment reference
 */
export function selectCloudAndCadAlignReference({
  cloudCadAlignmentMode,
}: RootState): AlignmentReference {
  return cloudCadAlignmentMode.alignmentReference;
}

/**
 * @returns the current cloud-CAD alignment step
 */
export function selectCloudCadAlignmentStep({
  cloudCadAlignmentMode,
}: RootState): CloudCadAlignmentStep {
  return cloudCadAlignmentMode.step;
}

/**
 * @param state  Current state
 * @returns ID of cloud used in current session of cloud-cad alignment
 */
export function selectCloudForCadAlignment(state: RootState): GUID | undefined {
  return state.cloudCadAlignmentMode.cloudIdForAlignment;
}

/**
 * @returns the current cloud-CAD alignment model elevation
 */
export function selectCloudCadAlignmentModelElevation({
  cloudCadAlignmentMode,
}: RootState): number {
  return cloudCadAlignmentMode.modelElevation;
}

/**
 * @returns the current cloud-CAD alignment cloud elevation
 */
export function selectCloudCadAlignmentCloudElevation({
  cloudCadAlignmentMode,
}: RootState): number {
  return cloudCadAlignmentMode.cloudElevation;
}

/**
 * @returns the current cloud-CAD alignment isModelClippingBoxEnabled
 */
export function selectModelClippingBoxEnabled({
  cloudCadAlignmentMode,
}: RootState): boolean {
  return cloudCadAlignmentMode.isModelClippingBoxEnabled;
}

/**
 * @returns the current flag isModelCrossSectionEnabled for cloud-cad alignment
 */
export function selectModelCrossSectionEnabled({
  cloudCadAlignmentMode,
}: RootState): boolean {
  return cloudCadAlignmentMode.isModelCrossSectionEnabled;
}

/**
 * @returns the current flag isCloudCrossSectionEnabled for cloud-cad alignment
 */
export function selectCloudCrossSectionEnabled({
  cloudCadAlignmentMode,
}: RootState): boolean {
  return cloudCadAlignmentMode.isCloudCrossSectionEnabled;
}

/**
 * @param state  Current state
 * @returns  anchor positions in split screen for cloud-cad alignment
 */
export function selectAnchorPositionsForCloudCadAlignment(
  state: RootState,
): TwoPointsPairsAlignmentAnchorPositions {
  return state.cloudCadAlignmentMode.alignmentAnchorPositions;
}

/**
 * @param state  Current state
 * @returns incremental cloud-cad transform in CS of previous pose or undefined if position/scale was not modified
 */
export function selectIncrementalCloudCadTransform(
  state: RootState,
): AlignmentTransform | undefined {
  return state.cloudCadAlignmentMode.incrementalTransform;
}

/**
 * @returns the current cloud-CAD alignment screen layout
 */
export function selectCloudCadAlignmentLayout({
  cloudCadAlignmentMode,
}: RootState): AlignmentViewLayout {
  return cloudCadAlignmentMode.alignmentLayout;
}

/**
 * @returns the current alignment screen split direction
 */
export function selectCloudCadAlignmentSplitDirection({
  cloudCadAlignmentMode,
}: RootState): SplitDirection {
  return cloudCadAlignmentMode.splitDirection;
}
